import React, { useContext, useEffect, useRef, useState } from 'react'

import InternshipsTable from '../../Components/shared/InternshipsManager/InternshipsTable'
import InternshipsHeader, { PRECISION_YEAR } from '../../Components/shared/InternshipsManager/InternshipsHeader'
import { bindActionCreators } from 'redux'
import { connect, useDispatch } from 'react-redux'
import { getAppStates } from '../../reducers/AppStates/actions'
import { DATE_WITHOUT_TIME, DIRECTION_DESC, internshipStates, ORDER_BY_CREATED_AT } from '../../utils/constants'
import { getUser } from '../../reducers/UserReducer'
import StudentInfo from '../../Components/shared/StudentInfo'
import InternshipInfo from '../../Components/shared/InternshipInfo'
import { startCheckingForMessages, stopCheckingForNewMessages } from '../../reducers/NewMessagesReducer'
import { deleteInternship, getAllInternships } from '../../utils/api/internship'
import { onError, onSuccess, purgeMetadata } from '../../utils/apiHelper'
import InternshipValidation from '../../Components/shared/ShiftsManager/InternshipValidation'
import { getState } from '../../reducers/InternshipsManagerReducer/actions'
import { INTERNSHIPS_MANAGER } from '../../reducers/InternshipsManagerReducer/actionsType'
import EarlyDateModal from '../../Components/shared/Modals/EarlyDateModal'
import { getTranslate } from 'react-localize-redux'
import { getInternshipsQuotas } from '../../utils/api/quota'
import InternshipRejection from '../../Components/shared/InternshipsManager/InternshipRejection'
import { APP_STATES } from '../../reducers/AppStates/actionsType'
import useLocalStorage from '../../hooks/UseLocalStorage'
import { InternshipsManagerContext } from '../../Context/InternshipsManager/InternshipsManagerProvider'
import { GlobalFiltersContext } from '../../Providers/GlobalFiltersProvider'

const { SET_DATE } = APP_STATES

const DEFAULT_NUMBER_OF_WEEKS = 24
const DEFAULT_META_DATA = {
  page: 1,
  last: 0,
  perPage: 10,
  totalPages: 1,
  total: 0
}
const TIME_BEFORE_SEARCH = 200
const INTERNSHIPS_NUMBER = 10

const SESSION_STORAGE_SEARCH_KEY = 'internships-manager-view.search'
const SESSION_STORAGE_STATES_KEY = 'internships-manager-view.states'
const SESSION_STORAGE_ORDER_BY_KEY = 'internships-manager-view.order-by'
const SESSION_STORAGE_SORT_DIRECTION_KEY = 'internships-manager-view.sort-direction'
const SESSION_STORAGE_PRECISION_KEY = 'internships-manager-view.precision'

const mapStateToProps = state => ({
  currentDate: getAppStates(state).date,
  refusedInternship: getState(state).refusedInternship,
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

const mapDispatchToProps = dispatch => ({
  startCheckingForMessages: bindActionCreators(startCheckingForMessages, dispatch),
  stopCheckingForNewMessages: bindActionCreators(stopCheckingForNewMessages, dispatch)
})

const InternshipsManagerView = props => {
  const { dataProvider } = useContext(InternshipsManagerContext)
  const { internshipSearch } = useContext(GlobalFiltersContext)

  const [addMode, setAddMode] = useState(false)
  const [displayEarlyModal, setDisplayEarlyModal] = useState(false)
  const [internshipToValidate, setInternshipToValidate] = useState(null)
  const [internships, setInternships] = useState([])
  const [metadata, setMetadata] = useState(DEFAULT_META_DATA)
  const [orderBy, setOrderBy] = useLocalStorage(SESSION_STORAGE_ORDER_BY_KEY, ORDER_BY_CREATED_AT)
  const [page, setPage] = useState(1)
  const [precision, setPrecision] = useLocalStorage(SESSION_STORAGE_PRECISION_KEY, PRECISION_YEAR)
  const [quotas, setQuotas] = useState({})
  const [selectedInternship, setSelectedInternship] = useState(null)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [states, setStates] = useLocalStorage(SESSION_STORAGE_STATES_KEY, [])
  const [search, setSearch] = useLocalStorage(SESSION_STORAGE_SEARCH_KEY, '')
  const [sortDirection, setSortDirection] = useLocalStorage(SESSION_STORAGE_SORT_DIRECTION_KEY, DIRECTION_DESC)
  const [weeksShownCount, setWeeksShowCount] = useState(DEFAULT_NUMBER_OF_WEEKS)
  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()
  const fetchTimer = useRef()

  useEffect(() => {
    setSearch(internshipSearch)
  }, [internshipSearch, setSearch])

  useEffect(() => {
    if (props.user && typeof props.startCheckingForMessages === 'function') {
      props.startCheckingForMessages(props.user)
    }

    return () => {
      props.stopCheckingForNewMessages()
    }
  }, [props.user, props.startCheckingForMessages])

  useEffect(() => {
    if (props.user) {
      setLoading(true)

      const parameters = buildUrlParemeters({
        startDate: props.currentDate,
        weeks: weeksShownCount,
        search,
        states,
        orderBy,
        sortDirection,
        page,
        perPage: INTERNSHIPS_NUMBER
      })

      postponeInternshipsFetch(parameters, props.user)
    }
  }, [
    props.user, page, search, states,
    orderBy, sortDirection, weeksShownCount, props.currentDate
  ])

  const postponeInternshipsFetch = (urlParameters, user) => {
    if (typeof fetchTimer.current === 'number') {
      clearTimeout(fetchTimer.current)
    }

    fetchTimer.current = setTimeout(() => {
      fetchInternships(urlParameters, user)
    }, TIME_BEFORE_SEARCH)
  }

  const handleInternshipDeletion = internship => {
    deleteInternship(internship, props.user).then(json => {
      if (json?.data) {
        onSuccess(props.t('internships_manager.delete_internship_success'))
        refreshInternships()
      } else {
        onError(props.t('internships_manager.delete_internship_forbidden'))
      }
    })
  }

  const refreshInternships = () => {
    const parameters = buildUrlParemeters({
      startDate: props.currentDate,
      weeks: weeksShownCount,
      search,
      states,
      orderBy,
      sortDirection,
      page,
      perPage: INTERNSHIPS_NUMBER
    })

    fetchInternships(parameters, props.user)
  }

  const refreshQuotas = customPrecision => {
    const endDate = props.currentDate.clone()
      .add(weeksShownCount - 1, 'week')
      .endOf('isoWeek')

    fetchQuotas(
      internships,
      props.currentDate.format(DATE_WITHOUT_TIME),
      endDate.format(DATE_WITHOUT_TIME),
      props.user,
      { precision: customPrecision ?? precision }
    )
  }

  const buildUrlParemeters = parameters => {
    const endDate = parameters.startDate.clone()
      .add(parameters.weeks - 1, 'week')
      .endOf('isoWeek')

    return {
      startDate: parameters.startDate.format(DATE_WITHOUT_TIME),
      endDate: endDate.format(DATE_WITHOUT_TIME),
      search: parameters.search,
      states: parameters.states,
      orderBy: parameters.orderBy,
      orderDirection: parameters.sortDirection,
      page: parameters.page,
      perPage: parameters.perPage
    }
  }

  const fetchInternships = (urlParameters, user) => {
    getAllInternships(user, urlParameters)
      .then(json => {
        if (json) {
          if (json.data) {
            setInternships(json.data)

            if (json.data.length > 0) {
              fetchQuotas(
                json.data,
                urlParameters.startDate,
                urlParameters.endDate,
                user,
                { precision: precision }
              )
            }
          }

          if (json.meta) {
            setMetadata(purgeMetadata(json.meta.pages))
          }
        }

        setLoading(false)
      })
  }

  const fetchQuotas = (internships, startDate, endDate, user, parameter = {}) => {
    getInternshipsQuotas(internships, startDate, endDate, user, parameter)
      .then(json => {
        if (json.data) {
          setQuotas(json.data)
        }
      })
  }

  const handleInternshipValidation = () => {
    const updatedInternships = internships.map(internship => {
      if (internship.id === internshipToValidate.id) {
        return { ...internship, state: internshipStates.SCHEDULE_VALIDATED }
      }

      return internship
    })

    setInternships(updatedInternships)
    setInternshipToValidate({ ...internshipToValidate, state: internshipStates.SCHEDULE_VALIDATED })
  }

  const handleInternshipInvalidation = newState => {
    const updatedInternships = internships.map(internship => {
      if (internship.id === internshipToValidate.id) {
        return { ...internship, state: newState }
      }

      return internship
    })

    setInternships(updatedInternships)
    setInternshipToValidate({ ...internshipToValidate, state: newState })
  }

  const handleInternshipRefused = () => {
    refreshInternships()
  }

  const handleRejectionClosing = () => {
    dispatch({
      type: INTERNSHIPS_MANAGER.SET_REFUSED_INTERNSHIP,
      payload: null
    })
  }

  const toggleAddMode = () => {
    setAddMode(!addMode)
  }

  const handleSearch = text => {
    setPage(1)
    setSearch(text)
  }

  const handleStates = array => {
    setPage(1)
    setStates(array)
  }

  const handleWeeksShowChange = newWeeks => {
    setPage(1)
    setWeeksShowCount(newWeeks)
  }

  const handleDateChange = newDate => {
    setPage(1)
    dispatch({
      type: SET_DATE,
      payload: newDate
    })
  }

  const handleResetFilters = () => {
    setPage(1)
    setSearch('')
    setStates([])
    setOrderBy(ORDER_BY_CREATED_AT)
    setSortDirection(DIRECTION_DESC)
    setPrecision(PRECISION_YEAR)
    setWeeksShowCount(DEFAULT_NUMBER_OF_WEEKS)
  }

  return (
    <>
      <InternshipsHeader
        addMode={addMode}
        states={states}
        search={search}
        orderBy={orderBy}
        sortDirection={sortDirection}
        page={page}
        precision={precision}
        metadata={metadata}
        weeksShownCount={weeksShownCount}
        loadShiftsManager={props.loadShiftsManager}
        onDateChange={handleDateChange}
        onOrderByChange={setOrderBy}
        onPageChange={setPage}
        onPrecisionChange={newPrecision => {
          setPrecision(newPrecision)
          refreshQuotas(newPrecision)
        }}
        onSearchChange={handleSearch}
        onSortDirectionChange={setSortDirection}
        onStatesChange={handleStates}
        onWeeksShowChange={handleWeeksShowChange}
        toggleAddMode={toggleAddMode}
        onResetFilters={handleResetFilters}
      />
      {dataProvider.Provider !== null && (
        <dataProvider.Provider
          addMode={addMode}
          internships={internships}
          quotas={quotas}
          weeksShownCount={weeksShownCount}
          onEarlyStartDate={setDisplayEarlyModal}
          onInternshipDelete={handleInternshipDeletion}
          onInternshipSelected={setSelectedInternship}
          onStudentSelected={setSelectedStudent}
          refreshInternships={refreshInternships}
          refreshQuotas={refreshQuotas}
          setInternshipToValidate={setInternshipToValidate}
        >
          <InternshipsTable loading={loading} customContext={dataProvider.context} />
        </dataProvider.Provider>
      )}

      <StudentInfo
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
      />
      <InternshipInfo
        internship={selectedInternship}
        onClose={() => setSelectedInternship(null)}
      />
      {internshipToValidate && (
        <InternshipValidation
          internship={internshipToValidate}
          onClose={() => setInternshipToValidate(null)}
          onValidate={handleInternshipValidation}
          onInvalidate={handleInternshipInvalidation}
        />
      )}
      <InternshipRejection
        internship={props.refusedInternship}
        onRejection={handleInternshipRefused}
        onClose={handleRejectionClosing}
      />
      {displayEarlyModal && (
        <EarlyDateModal
          t={props.t}
          onVisibleChange={setDisplayEarlyModal}
          visible
        />
      )}
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(InternshipsManagerView)
